import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'

export default function WidgetClients() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const navigate = useNavigate()
  const token = sessionStorage.getItem('tredex_widget_token')

  const loadClients = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/widget/admin/clients', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await res.json()
      if (res.status === 401) {
        sessionStorage.removeItem('tredex_widget_token')
        navigate('/admin/widget/login', { replace: true })
        return
      }
      if (!res.ok) throw new Error(data.error || 'Failed to load clients')
      setClients(data.clients || [])
    } catch (err) {
      setMessage(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadClients() }, [])

  const copyEmbed = async (clientId) => {
    try {
      const res = await fetch(`/api/widget/admin/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to load embed code')
      await navigator.clipboard.writeText(data.embedCode)
      setMessage(`Embed code for "${clientId}" copied to clipboard!`)
    } catch (err) {
      setMessage(err.message)
    }
  }

  const handleDelete = async (clientId) => {
    if (!confirm(`Delete this client (${clientId})? This cannot be undone.`)) return
    try {
      const res = await fetch(`/api/widget/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Delete failed')
      }
      setMessage('Client deleted.')
      loadClients()
    } catch (err) {
      setMessage(err.message)
    }
  }

  return (
    <>
      <SEO title="Widget Clients" noIndex />
      <Navbar />
      <div className="dashboard-page">
        <div className="container">
          <div className="dash-header">
            <div>
              <h1><i className="fas fa-comment-dots"></i> Widget Clients</h1>
              <p>Add clients and copy their one-line embed script.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <Link to="/admin/widget/clients/new" className="btn btn-primary">
                <i className="fas fa-plus"></i> Add New Client
              </Link>
              <button className="btn btn-outline" onClick={() => { sessionStorage.removeItem('tredex_widget_token'); navigate('/admin/widget/login') }}>
                <i className="fas fa-sign-out-alt"></i> Logout
              </button>
            </div>
          </div>

          {message && <div className="form-status success" style={{ display: 'block', marginBottom: '20px' }}>{message}</div>}

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '20px', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
             <strong>Page-aware AI:</strong> The widget can detect which product/page a visitor is viewing and who they are. To enable this, paste the embed script <em>plus</em> a small <code>window.tredexWidget.context</code> snippet on the client's pages. See the setup guide: <a href="/docs/widget-context" target="_blank" rel="noopener" style={{ color: 'var(--accent)', fontWeight: 600 }}>docs/widget-context</a>
          </div>

          <div className="admin-table-wrapper">
            {loading ? (
              <p style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading clients...</p>
            ) : clients.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '16px', display: 'block' }}></i>
                <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>No clients yet. Add your first client to get an embed script.</p>
                <Link to="/admin/widget/clients/new" className="btn btn-primary"><i className="fas fa-plus"></i> Add New Client</Link>
              </div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Client ID</th>
                    <th>Business Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => (
                    <tr key={c.clientId}>
                      <td><code>{c.clientId}</code></td>
                      <td>{c.businessName}</td>
                      <td>
                        <div className="admin-actions">
                          <button className="btn btn-sm btn-primary" onClick={() => copyEmbed(c.clientId)} title="Copy embed code"><i className="fas fa-code"></i> Copy Embed</button>
                          <Link className="btn btn-sm btn-outline" to={`/admin/widget/clients/${c.clientId}/edit`} title="Edit"><i className="fas fa-edit"></i></Link>
                          <Link className="btn btn-sm btn-outline" to={`/admin/widget/clients/${c.clientId}/leads`} title="Leads"><i className="fas fa-chart-line"></i></Link>
                          <button className="btn btn-sm btn-outline" style={{ borderColor: '#FF5F56', color: '#FF5F56' }} onClick={() => handleDelete(c.clientId)} title="Delete"><i className="fas fa-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}
